<template>
  <v-card>
    <v-card-title class="primary">
      <span class="tertiary--text headline">{{ propProfile.name }}</span>
      <v-spacer/>
      <BaseDialogActions hideRefresh>
        <template #left>
          <v-menu bottom left>
            <template #activator="{ on }">
              <v-icon v-on="on" color="tertiary">mdi-dots-vertical</v-icon>
            </template>
            <v-list>
              <v-list-item v-for="(ha, index) in headerActions" :key="`${ha.text}-${index}`" @click="ha.action()" :disabled="ha.disabled " :id="`account-detail-context-menu-${kebabCase(ha.text)}`">
                <v-avatar left>
                  <v-icon color="black" :disabled="ha.disabled">{{ha.icon}}</v-icon>
                </v-avatar>
                <v-list-item-title class="subtitle-1">{{ $t(ha.text) }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </BaseDialogActions>
    </v-card-title>
    <v-card-text>
      <v-container fluid>
        <v-tabs v-model="tab">
          <v-tab> {{ $t('general') }} </v-tab>
          <v-tab v-if="isCsvProfile">{{ $t('columns') }}</v-tab>
          <v-tab v-else-if="isDelimitedProfile">{{ $t('indices') }}</v-tab>
          <v-tab v-else-if="isFixedLengthProfile">{{ $t('placements') }}</v-tab>
          <v-tab v-if="propProfile.productProfiles.length > 0">{{ $t('products') }}</v-tab>
          <v-tab>{{ $t('species') }}</v-tab>
          <v-tab v-if="propProfile.gradeProfiles.length > 0">{{ $t('grades') }}</v-tab>
          <v-tab v-if="propProfile.endUseSortProfiles.length > 0">{{ $t('endUseSorts') }}</v-tab>
          <v-tab v-if="propProfile.accountProfiles.length > 0">{{ $t('accounts') }}</v-tab>
          <v-tab v-if="propProfile.settingProfiles.length > 0">{{ $t('settings') }}</v-tab>
          <v-tab v-if="propProfile.tractProfiles.length > 0">{{ $t('tracts') }}</v-tab>

          <v-tabs-items v-model="tab" class="mt-1" touchless>
            <v-tab-item>
              <v-container fluid grid-list-lg>
                <v-row dense class="mt-1">
                  <v-col cols="12" md="3" lg="2">
                    <v-text-field
                      v-model="fileType"
                      :label="$t('fileType')"
                      outlined
                      readonly
                      color="black"
                    />
                  </v-col>
                  <v-col cols="12" md="3" lg="2">
                    <v-text-field
                      v-model="propProfile.defaultInWeight"
                      :label="$t('defaultInWeight')"
                      outlined
                      readonly
                      color="black"
                    />
                  </v-col>
                  <v-col cols="12" md="3" lg="2">
                    <v-text-field
                      v-model="propProfile.defaultOutWeight"
                      :label="$t('defaultOutWeight')"
                      outlined
                      readonly
                      color="black"
                    />
                  </v-col>
                  <v-col cols="12" md="3" lg="2">
                    <v-text-field
                      v-model="propProfile.buttLogIndicator"
                      :label="$t('buttLogIndicator')"
                      outlined
                      readonly
                      color="black"
                    />
                  </v-col>
                  <v-col cols="12" md="3" lg="2">
                    <v-text-field
                      v-model="propProfile.multiplicationFactor"
                      :label="$t('multiplicationFactor')"
                      outlined
                      readonly
                      color="black"
                    />
                  </v-col>
                  <v-col cols="12" md="3" lg="2">
                    <v-text-field
                      v-model="profileMapped.gradeName"
                      :label="$t('defaultGrade')"
                      outlined
                      readonly
                      color="black"
                    />
                  </v-col>
                  <v-col cols="12" md="3" lg="2">
                    <v-text-field
                      v-model="profileMapped.settingName"
                      :label="$t('defaultSetting')"
                      outlined
                      readonly
                      color="black"
                    />
                  </v-col>
                  <v-col cols="12" md="3" lg="2">
                    <v-text-field
                      v-model="profileMapped.defaultTrailerIdentifier"
                      :label="$t('defaultTrailerIdentifier')"
                      outlined
                      readonly
                      color="black"
                    />
                  </v-col>
                </v-row>
              </v-container>
            </v-tab-item>
            <v-tab-item v-if="isCsvProfile">
              <ScaleSheetImportCsvProfileDetail
                :propProfile="propProfile.scaleSheetImportCsvProfile"
              />
            </v-tab-item>
            <v-tab-item v-else-if="isDelimitedProfile">
              <ScaleSheetImportDelimitedProfileDetail
                :propProfile="propProfile.scaleSheetImportDelimitedProfile"
              />
            </v-tab-item>
            <v-tab-item v-else-if="isFixedLengthProfile">
              <ScaleSheetImportFixedLengthProfileDetail
                :propProfile="propProfile.scaleSheetImportFixedLengthProfile"
              />
            </v-tab-item>

            <!-- Product Profiles Tab -->
            <v-tab-item v-if="propProfile.productProfiles.length > 0">
              <v-container fluid>
                <v-row dense>
                  <v-col v-for="(product, index) in productProfilesMapped" :key="index + 'pp'" cols="12" md="6" lg="3">
                    <v-card outlined rounded class="fill-height">
                      <v-card-title>
                        <v-row align="center">
                          <v-col cols="auto">
                            <span class="subtitle-1">
                              {{ $t('productProfile') }} {{ index + 1 }}
                            </span>
                          </v-col>
                        </v-row>
                      </v-card-title>
                      <v-card-text>
                        <v-row>
                          <v-col cols="6">
                            <v-text-field
                              v-model="product.externalProductName"
                              :label="$t('externalProductName')"
                              outlined
                              readonly
                              color="black"
                            />
                          </v-col>
                          <v-col cols="6">
                            <v-text-field
                              v-model="product.productName"
                              :label="$t('product')"
                              outlined
                              readonly
                              color="black"
                            />
                          </v-col>
                        </v-row>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </v-container>
            </v-tab-item>

            <!-- Species Profiles Tab -->
            <v-tab-item>
              <v-container fluid>
                <v-row dense>
                  <v-col v-for="(species, index) in propProfile.speciesProfiles" :key="index + 'sp'" cols="12" md="6" lg="3">
                    <v-card outlined rounded class="fill-height">
                      <v-card-title>
                        <v-row align="center">
                          <v-col cols="auto">
                            <span class="subtitle-1">
                              {{ $t('speciesProfile') }} {{ index + 1 }}
                            </span>
                          </v-col>
                        </v-row>
                      </v-card-title>
                      <v-card-text>
                        <v-row>
                          <v-col cols="6">
                            <v-text-field
                              v-model="species.externalSpeciesCode"
                              :label="$t('speciesCode')"
                              outlined
                              readonly
                              color="black"
                            />
                          </v-col>
                          <v-col cols="6">
                            <v-text-field
                              v-model="species.species.commonName"
                              :label="$t('species')"
                              outlined
                              readonly
                              color="black"
                            />
                          </v-col>
                        </v-row>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </v-container>
            </v-tab-item>

            <!-- Grade Profiles Tab -->
            <v-tab-item v-if="propProfile.gradeProfiles.length > 0">
              <v-container fluid>
                <v-row dense>
                  <v-col v-for="(grade, index) in propProfile.gradeProfiles" :key="index + 'gp'" cols="12" md="6" lg="3">
                    <v-card outlined rounded class="fill-height">
                      <v-card-title>
                        <v-row align="center">
                          <v-col cols="auto">
                            <span class="subtitle-1">
                              {{ $t('gradeProfile') }} {{ index + 1 }}
                            </span>
                          </v-col>
                        </v-row>
                      </v-card-title>
                      <v-card-text>
                        <v-row>
                          <v-col cols="6">
                            <v-text-field
                              v-model="grade.externalGradeCode"
                              :label="$t('externalGrade')"
                              outlined
                              readonly
                              color="black"
                            />
                          </v-col>
                          <v-col cols="6">
                            <v-text-field
                              v-model="grade.grade.name"
                              :label="$t('grade')"
                              outlined
                              readonly
                              color="black"
                            />
                          </v-col>
                        </v-row>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </v-container>
            </v-tab-item>

            <!-- End Use Sort Profiles Tab -->
            <v-tab-item v-if="propProfile.endUseSortProfiles.length > 0">
              <v-container fluid>
                <v-row dense>
                  <v-col v-for="(endUseSort, index) in propProfile.endUseSortProfiles" :key="index + 'eus'" cols="12" md="6" lg="3">
                    <v-card outlined rounded class="fill-height">
                      <v-card-title>
                        <v-row align="center">
                          <v-col cols="auto">
                            <span class="subtitle-1">
                              {{ $t('endUseSortProfile') }} {{ index + 1 }}
                            </span>
                          </v-col>
                        </v-row>
                      </v-card-title>
                      <v-card-text>
                        <v-row>
                          <v-col cols="6">
                            <v-text-field
                              v-model="endUseSort.externalEndUseSortCode"
                              :label="$t('externalEndUseSort')"
                              outlined
                              readonly
                              color="black"
                            />
                          </v-col>
                          <v-col cols="6">
                            <v-text-field
                              v-model="endUseSort.endUseSort.name"
                              :label="$t('endUseSort')"
                              outlined
                              readonly
                              color="black"
                            />
                          </v-col>
                        </v-row>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </v-container>
            </v-tab-item>

            <!-- Account Profiles Tab -->
            <v-tab-item v-if="propProfile.accountProfiles.length > 0">
              <v-container fluid>
                <v-row dense>
                  <v-col v-for="(account, index) in accountProfilesMapped" :key="index + 'ap'" cols="12" md="6" lg="3">
                    <v-card outlined rounded class="fill-height">
                      <v-card-title>
                        <v-row align="center">
                          <v-col cols="auto">
                            <span class="subtitle-1">
                              {{ $t('accountProfile') }} {{ index + 1 }}
                            </span>
                          </v-col>
                        </v-row>
                      </v-card-title>
                      <v-card-text>
                        <v-row>
                          <v-col cols="6">
                            <v-text-field
                              v-model="account.externalAccountName"
                              :label="$t('externalAccountName')"
                              outlined
                              readonly
                              color="black"
                            />
                          </v-col>
                          <v-col cols="6">
                            <v-text-field
                              v-model="account.accountName"
                              :label="$t('account')"
                              outlined
                              readonly
                              color="black"
                            />
                          </v-col>
                        </v-row>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </v-container>
            </v-tab-item>

            <!-- Setting Profiles Tab -->
            <v-tab-item v-if="propProfile.settingProfiles.length > 0">
              <v-container fluid>
                <v-row dense>
                  <v-col v-for="(setting, index) in settingProfilesMapped" :key="index + 'sp'" cols="12" md="6" lg="3">
                    <v-card outlined rounded class="fill-height">
                      <v-card-title>
                        <v-row align="center">
                          <v-col cols="auto">
                            <span class="subtitle-1">
                              {{ $t('settingProfile') }} {{ index + 1 }}
                            </span>
                          </v-col>
                        </v-row>
                      </v-card-title>
                      <v-card-text>
                        <v-row>
                          <v-col cols="6">
                            <v-text-field
                              v-model="setting.externalSettingName"
                              :label="$t('externalSettingName')"
                              outlined
                              readonly
                              color="black"
                            />
                          </v-col>
                          <v-col cols="6">
                            <v-text-field
                              v-model="setting.settingName"
                              :label="$t('setting')"
                              outlined
                              readonly
                              color="black"
                            />
                          </v-col>
                        </v-row>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </v-container>
            </v-tab-item>

            <!-- Tract Profiles Tab -->
            <v-tab-item v-if="propProfile.tractProfiles.length > 0">
              <v-container fluid>
                <v-row dense>
                  <v-col v-for="(tract, index) in tractProfilesMapped" :key="index + 'tp'" cols="12" md="6" lg="3">
                    <v-card outlined rounded class="fill-height">
                      <v-card-title>
                        <v-row align="center">
                          <v-col cols="auto">
                            <span class="subtitle-1">
                              {{ $t('tractProfile') }} {{ index + 1 }}
                            </span>
                          </v-col>
                        </v-row>
                      </v-card-title>
                      <v-card-text>
                        <v-row>
                          <v-col cols="6">
                            <v-text-field
                              v-model="tract.externalTractName"
                              :label="$t('externalTractName')"
                              outlined
                              readonly
                              color="black"
                            />
                          </v-col>
                          <v-col cols="6">
                            <v-text-field
                              v-model="tract.tractName"
                              :label="$t('tract')"
                              outlined
                              readonly
                              color="black"
                            />
                          </v-col>
                        </v-row>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </v-container>
            </v-tab-item>
          </v-tabs-items>
        </v-tabs>
      </v-container>
    </v-card-text>
  </v-card>
</template>

<script>
import { ScaleSheetImportFileType } from '@/utils/Enumerations'
import { kebabCase } from 'lodash'

export default {
  name: 'ScaleSheetDetail',

  props: {
    propProfile: {
      type: Object,
      required: true
    },
    propGrades: {
      type: Array,
      required: true
    },
    propAccounts: {
      type: Array,
      required: true
    },
    propSettings: {
      type: Array,
      required: true
    },
    propTracts: {
      type: Array,
      required: true
    },
    propProducts: {
      type: Array,
      required: true
    }
  },

  data: () => ({
    tab: 0
  }),

  components: {
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue'),
    ScaleSheetImportCsvProfileDetail: () => import('./ScaleSheetImportCsvProfileDetail.vue'),
    ScaleSheetImportDelimitedProfileDetail: () => import('./ScaleSheetImportDelimitedProfileDetail.vue'),
    ScaleSheetImportFixedLengthProfileDetail: () => import('./ScaleSheetImportFixedLengthProfileDetail.vue')
  },

  computed: {
    isCsvProfile () {
      return this.propProfile.scaleSheetImportFileType === ScaleSheetImportFileType.Csv.value
    },
    isDelimitedProfile () {
      return this.propProfile.scaleSheetImportFileType === ScaleSheetImportFileType.Delimited.value
    },
    isFixedLengthProfile () {
      return this.propProfile.scaleSheetImportFileType === ScaleSheetImportFileType.FixedLength.value
    },
    fileType () {
      return ScaleSheetImportFileType.fromInt(this.propProfile.scaleSheetImportFileType)
    },
    accountProfilesMapped () {
      return this.propProfile.accountProfiles.map(ap => {
        return {
          ...ap,
          accountName: this.getAccountName(ap.accountId)
        }
      })
    },
    settingProfilesMapped () {
      return this.propProfile.settingProfiles.map(sp => {
        return {
          ...sp,
          settingName: this.getSettingName(sp.settingId)
        }
      })
    },
    tractProfilesMapped () {
      return this.propProfile.tractProfiles.map(tp => {
        return {
          ...tp,
          tractName: this.getTractName(tp.tractId)
        }
      })
    },
    productProfilesMapped () {
      return this.propProfile.productProfiles.map(pp => {
        return {
          ...pp,
          productName: this.getProductName(pp.productId)
        }
      })
    },
    profileMapped () {
      return {
        ...this.propProfile,
        gradeName: this.getGradeName(this.propProfile.defaultGradeId),
        settingName: this.getSettingName(this.propProfile.defaultSettingId)
      }
    },

    headerActions () {
      const actions = (this.propProfile) ? [
        {
          text: 'editScaleSheetProfile',
          icon: 'mdi-pencil',
          action: () => this.$emit('edit-scale-sheet-profile', this.propProfile),
          disabled: false
        },
        {
          text: 'exportScaleSheetProfile',
          icon: 'mdi-export',
          action: () => this.$emit('export-scale-sheet-profile', this.propProfile),
          disabled: false
        }
      ] : []
      return actions
    }
  },

  methods: {
    kebabCase,

    closeDialog () {
      this.$emit('close')
    },
    getAccountName (accountId) {
      const account = this.propAccounts.find(a => a.accountId === accountId)
      return account ? account.name : ''
    },
    getSettingName (settingId) {
      const setting = this.propSettings.find(s => s.settingId === settingId)
      return setting ? setting.name : ''
    },
    getTractName (tractId) {
      const tract = this.propTracts.find(t => t.tractId === tractId)
      return tract ? tract.name : ''
    },
    getGradeName (gradeId) {
      const grade = this.propGrades.find(g => g.gradeId === gradeId)
      return grade ? grade.name : ''
    },
    getProductName (productId) {
      const product = this.propProducts.find(p => p.productId === productId)
      return product ? product.name : ''
    }
  }
}
</script>
